import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthProvider';
import { OrganizationSignup } from './OrganizationSignup';
import { TokenService } from '@/services/tokenService';
import { User, Building, AlertCircle, Eye, EyeOff, CheckCircle2, XCircle, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';


export const EnhancedAuthForm = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [mode, setMode] = useState<'auth' | 'org-signup'>('auth');
  const [isLogin, setIsLogin] = useState(true);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // ─── INVITE CONTEXT ──────────────────────────────────────────────────────────
  const [inviteContext, setInviteContext] = useState<{
    isInvite: boolean;
    orgId: string | null;
    invitedEmail: string | null;
    invitedName: string | null;
    invitedRole: string | null;
  }>({ isInvite: false, orgId: null, invitedEmail: null, invitedName: null, invitedRole: null });

  const [passwordStrength, setPasswordStrength] = useState({
    hasMinLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false
  });
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
    confirmPassword: ''
  });

  // ─── READ INVITE PARAMS FROM URL ON MOUNT ────────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isInvite = params.get('invite') === 'true';
    const invitedEmail = params.get('email');
    const orgId = params.get('org');
    const invitedName = params.get('name');   // pre-fill full name
    const invitedRole = params.get('role');   // passed to edge fn for user_profiles

    if (isInvite && invitedEmail) {
      setInviteContext({ isInvite: true, orgId, invitedEmail, invitedName, invitedRole });
      setFormData(prev => ({
        ...prev,
        email: invitedEmail,
        fullName: invitedName || '',  // pre-fill name field
      }));
      setIsLogin(false); // switch to signup tab
      return;
    }

    const loginEmail = params.get('email');
    if (loginEmail) {
      setFormData(prev => ({
        ...prev,
        email: loginEmail,
      }));
    }
  }, []);

  useEffect(() => {
    if (user) {
      window.location.href = '/';
    }
  }, [user]);

  useEffect(() => {
    if (formData.password) {
      setPasswordStrength({
        hasMinLength: formData.password.length >= 8,
        hasUpperCase: /[A-Z]/.test(formData.password),
        hasLowerCase: /[a-z]/.test(formData.password),
        hasNumber: /[0-9]/.test(formData.password),
        hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(formData.password)
      });
    }
  }, [formData.password]);

  const isPasswordStrong = () => {
    return Object.values(passwordStrength).every(v => v === true);
  };

  // ─── SEND EMAIL HELPER ───────────────────────────────────────────────────────
  const sendEmail = async (to: string, subject: string, html: string) => {
    const { data, error } = await supabase.functions.invoke("send-email", {
      body: { to, subject, html },
    });
    if (error) throw new Error(`Failed to send email: ${error.message}`);
    if (data && !data.success) throw new Error(data.error || "Failed to send email");
    return data;
  };

  // ─── SIGN IN ─────────────────────────────────────────────────────────────────
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      });

      if (error) {
        const normalizedMessage = error.message.toLowerCase();
        if (normalizedMessage.includes('email not confirmed')) {
          navigate(`/resend-verification?email=${encodeURIComponent(formData.email)}`, {
            replace: true,
          });
          return;
        }
        if (error.message.includes('Invalid login credentials')) {
          throw new Error('Invalid email or password. Please check your credentials and try again.');
        }
        throw error;
      }

      toast({
        title: "Welcome back!",
        description: "You have been successfully logged in.",
      });
    } catch (error) {
      toast({
        title: "Login Failed",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // ─── SIGN UP ─────────────────────────────────────────────────────────────────
  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email || !formData.password || !formData.fullName) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Password Mismatch",
        description: "Passwords do not match",
        variant: "destructive",
      });
      return;
    }

    if (!isPasswordStrong()) {
      toast({
        title: "Weak Password",
        description: "Please meet all password requirements",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      // Step 1: Create account via custom-signup edge function
      const { data: signUpData, error: signUpError } = await supabase.functions.invoke("custom-signup", {
        body: {
          email: formData.email,
          password: formData.password,
          fullName: formData.fullName
        },
      });

      if (signUpError) {
        let errorMessage = "An unexpected error occurred";
        try {
          const errorBody = await signUpError.context.json();
          errorMessage = errorBody.error || signUpError.message;
        } catch {
          errorMessage = signUpError.message;
        }
        throw new Error(errorMessage);
      }

      if (signUpData?.error) {
        throw new Error(signUpData.error);
      }

      // Step 2: Generate email verification token
      const { success, token, error: tokenError } = await TokenService.createEmailVerificationToken(formData.email);

      if (!success || !token) {
        console.error("Token creation failed:", tokenError);
        toast({
          title: "Account Created!",
          description: "Please check your email to verify your account.",
        });
        setFormData({ email: '', password: '', fullName: '', confirmPassword: '' });
        return;
      }

      // Step 3: Build verification link
      // KEY CHANGE: pass &org= if coming from an invite so VerifyEmailPage
      // can activate the team_members row after verification
      const verificationLink = inviteContext.isInvite && inviteContext.orgId
        ? `${window.location.origin}/verify-email?token=${token}&org=${inviteContext.orgId}&role=${encodeURIComponent(inviteContext.invitedRole || '')}`
        : `${window.location.origin}/verify-email?token=${token}`;

      // Step 4: Send branded verification email
      await sendEmail(
        formData.email,
        "Verify your GreenCard account",
        `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
            <div style="text-align: center; margin-bottom: 30px;">
              <h1 style="color: #16a34a; margin: 0;">GreenCard Sustainability Manager</h1>
              <p style="color: #666; margin: 4px 0 0;">Sustainable Business Intelligence Platform</p>
            </div>

            <h2 style="color: #1a1a1a;">Welcome, ${formData.fullName}! 👋</h2>

            ${inviteContext.isInvite
              ? `<p style="color: #444; line-height: 1.6;">
                  You've been invited to join a GreenCard organization.
                  Verify your email to activate your account and join the team.
                </p>`
              : `<p style="color: #444; line-height: 1.6;">
                  Thanks for signing up. Please verify your email address to activate your account.
                </p>`
            }

            <div style="text-align: center; margin: 30px 0;">
              <a href="${verificationLink}"
                style="display: inline-block; padding: 14px 32px; background-color: #16a34a; color: white;
                       text-decoration: none; border-radius: 8px; font-size: 16px; font-weight: bold;">
                Verify Email Address
              </a>
            </div>

            <p style="color: #666; font-size: 14px; line-height: 1.6;">
              This link will expire in <strong>24 hours</strong>.
              If you didn't create an account, you can safely ignore this email.
            </p>

            <hr style="border: none; border-top: 1px solid #eee; margin: 30px 0;" />
            <p style="color: #999; font-size: 12px; text-align: center;">
              GreenCard Sustainability Platform · If the button doesn't work, copy this link:<br/>
              <a href="${verificationLink}" style="color: #16a34a;">${verificationLink}</a>
            </p>
          </div>
        `
      );

      toast({
        title: "Account Created!",
        description: "We've sent a verification link to your email. Please check your inbox.",
      });

      setFormData({ email: '', password: '', fullName: '', confirmPassword: '' });
      setIsLogin(true);

    } catch (error) {
      console.error('Signup error:', error);
      toast({
        title: "Signup Failed",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // ─── FORGOT PASSWORD ─────────────────────────────────────────────────────────
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email) {
      toast({
        title: "Email Required",
        description: "Please enter your email address to reset your password",
        variant: "destructive",
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { success, token, error } = await TokenService.createPasswordResetToken(formData.email);

      if (!success) {
        toast({
          title: "Check Your Email",
          description: "If an account exists with this email, you will receive a password reset link shortly.",
        });
        setIsForgotPassword(false);
        setFormData({ email: '', password: '', fullName: '', confirmPassword: '' });
        return;
      }

      if (!token) throw new Error('Failed to generate reset token');

      const resetLink = `${window.location.origin}/reset-password?token=${token}`;

      await sendEmail(
        formData.email,
        "Password Reset Request - GreenCard",
        `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
            <div style="text-align: center; margin-bottom: 30px;">
              <h1 style="color: #16a34a; margin: 0;">GreenCard Sustainability Manager</h1>
              <p style="color: #666; margin: 4px 0 0;">Sustainable Business Intelligence Platform</p>
            </div>
            <h2 style="color: #1a1a1a;">Reset your password</h2>
            <p style="color: #444; line-height: 1.6;">
              We received a request to reset the password for your GreenCard account.
              Click the button below to choose a new password.
            </p>
            <div style="text-align: center; margin: 30px 0;">
              <a href="${resetLink}"
                style="display: inline-block; padding: 14px 32px; background-color: #16a34a; color: white;
                       text-decoration: none; border-radius: 8px; font-size: 16px; font-weight: bold;">
                Reset Password
              </a>
            </div>
            <p style="color: #666; font-size: 14px; line-height: 1.6;">
              This link will expire in <strong>1 hour</strong>. If you didn't request a password reset,
              you can safely ignore this email.
            </p>
            <hr style="border: none; border-top: 1px solid #eee; margin: 30px 0;" />
            <p style="color: #999; font-size: 12px; text-align: center;">
              GreenCard Sustainability Platform · If the button doesn't work, copy this link:<br/>
              <a href="${resetLink}" style="color: #16a34a;">${resetLink}</a>
            </p>
          </div>
        `
      );

      toast({
        title: "Reset Link Sent!",
        description: "Check your email for the password reset link. The link will expire in 1 hour.",
      });

      setIsForgotPassword(false);
      setFormData({ email: '', password: '', fullName: '', confirmPassword: '' });

    } catch (error) {
      console.error('Error in forgot password flow:', error);
      toast({
        title: "Error Sending Email",
        description: error instanceof Error ? error.message : "Failed to send reset email. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // ─── PASSWORD STRENGTH INDICATOR ─────────────────────────────────────────────
  const PasswordStrengthIndicator = () => (
    <div className="space-y-2 mt-2 text-xs">
      <p className="font-semibold text-gray-700">Password requirements:</p>
      <div className="space-y-1">
        {[
          { key: 'hasMinLength', label: 'At least 8 characters' },
          { key: 'hasUpperCase', label: 'One uppercase letter' },
          { key: 'hasLowerCase', label: 'One lowercase letter' },
          { key: 'hasNumber', label: 'One number' },
          { key: 'hasSpecialChar', label: 'One special character (!@#$%^&*...)' },
        ].map(({ key, label }) => (
          <div key={key} className="flex items-center gap-2">
            {passwordStrength[key as keyof typeof passwordStrength] ? (
              <CheckCircle2 className="h-3 w-3 text-green-600" />
            ) : (
              <XCircle className="h-3 w-3 text-gray-400" />
            )}
            <span className={passwordStrength[key as keyof typeof passwordStrength] ? "text-green-600" : "text-gray-600"}>
              {label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  // ─── ORGANIZATION SIGNUP MODE ─────────────────────────────────────────────────
  if (mode === 'org-signup') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
        <OrganizationSignup
          onBack={() => setMode('auth')}
          onSuccess={() => setMode('auth')}
        />
      </div>
    );
  }

  // ─── MAIN AUTH FORM ───────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">

        {/* Logo and Header */}
        <div className="text-center">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-green-600 p-3 rounded-full">
              <img src="/greencard-uploads/logo.png" alt="GreenCard Logo" className="h-8 w-8 rounded-full bg-white object-contain" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">GreenCard Sustainability Manager</h1>
          <p className="text-gray-600 mt-2">Sustainable Business Intelligence Platform</p>
        </div>

        {/* Invite Banner - only shown when coming from team invite */}
        {inviteContext.isInvite && (
          <Alert className="border-green-200 bg-green-50">
            <Mail className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">
              You've been invited to join a GreenCard organization. Complete your signup below to accept the invitation.
            </AlertDescription>
          </Alert>
        )}

        {/* Account Type Selection - hidden when coming from invite */}
        {!inviteContext.isInvite && (
          <div className="grid grid-cols-2 gap-4">
            <Card
              className="cursor-pointer hover:shadow-md transition-shadow border-2 border-transparent hover:border-green-200"
              onClick={() => setMode('auth')}
            >
              <CardContent className="p-4 text-center">
                <User className="h-8 w-8 mx-auto mb-2 text-green-600" />
                <h3 className="font-semibold">Individual</h3>
                <p className="text-sm text-gray-600">Personal sustainability tracking</p>
              </CardContent>
            </Card>

            <Card
              className="cursor-pointer hover:shadow-md transition-shadow border-2 border-transparent hover:border-blue-200"
              onClick={() => setMode('org-signup')}
            >
              <CardContent className="p-4 text-center">
                <Building className="h-8 w-8 mx-auto mb-2 text-blue-600" />
                <h3 className="font-semibold">Organization</h3>
                <p className="text-sm text-gray-600">Team collaboration & management</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Auth Form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-center">
              {inviteContext.isInvite ? 'Accept Invitation' : 'Individual Account'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs
              value={isLogin ? "signin" : "signup"}
              onValueChange={(value) => {
                setIsLogin(value === "signin");
                setIsForgotPassword(false);
              }}
            >
              <TabsList className="grid w-full grid-cols-2">
                {/* Disable Sign In tab when coming from invite - they must sign up first */}
                <TabsTrigger value="signin" disabled={inviteContext.isInvite}>
                  Sign In
                </TabsTrigger>
                <TabsTrigger value="signup">Sign Up</TabsTrigger>
              </TabsList>

              {/* ── SIGN IN TAB ── */}
              <TabsContent value="signin" className="space-y-4 mt-6">
                {isForgotPassword ? (
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        Enter your email address and we'll send you a link to reset your password.
                      </AlertDescription>
                    </Alert>
                    <div>
                      <Label htmlFor="resetEmail">Email</Label>
                      <Input
                        id="resetEmail"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="Enter your email"
                        required
                      />
                    </div>
                    <Button type="submit" className="w-full" disabled={loading}>
                      {loading ? 'Sending Reset Link...' : 'Send Reset Link'}
                    </Button>
                    <Button
                      type="button"
                      variant="link"
                      className="w-full"
                      onClick={() => {
                        setIsForgotPassword(false);
                        setFormData({ email: '', password: '', fullName: '', confirmPassword: '' });
                      }}
                    >
                      Back to Sign In
                    </Button>
                  </form>
                ) : (
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="Enter your email"
                        required
                      />
                    </div>
                    <div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor="password">Password</Label>
                        <Button
                          type="button"
                          variant="link"
                          className="px-0 h-auto text-xs"
                          onClick={() => setIsForgotPassword(true)}
                        >
                          Forgot password?
                        </Button>
                      </div>
                      <div className="relative">
                        <Input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          value={formData.password}
                          onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                          placeholder="Enter your password"
                          required
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>
                    <Button type="submit" className="w-full" disabled={loading}>
                      {loading ? 'Signing In...' : 'Sign In'}
                    </Button>
                  </form>
                )}
              </TabsContent>

              {/* ── SIGN UP TAB ── */}
              <TabsContent value="signup" className="space-y-4 mt-6">
                <form onSubmit={handleSignup} className="space-y-4">
                  <div>
                    <Label htmlFor="fullName">Full Name</Label>
                    <Input
                      id="fullName"
                      value={formData.fullName}
                      onChange={(e) => setFormData(prev => ({ ...prev, fullName: e.target.value }))}
                      placeholder="Enter your full name"
                      readOnly={inviteContext.isInvite && !!inviteContext.invitedName}
                      className={inviteContext.isInvite && inviteContext.invitedName ? "bg-gray-50 cursor-not-allowed" : ""}
                      required
                    />
                    {inviteContext.isInvite && inviteContext.invitedName && (
                      <p className="text-xs text-gray-500 mt-1">Name is pre-filled from your invitation.</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="signupEmail">Email</Label>
                    <Input
                      id="signupEmail"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="Enter your email"
                      readOnly={inviteContext.isInvite}
                      className={inviteContext.isInvite ? "bg-gray-50 cursor-not-allowed" : ""}
                      required
                    />
                    {inviteContext.isInvite && (
                      <p className="text-xs text-gray-500 mt-1">
                        Email is pre-filled from your invitation and cannot be changed.
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="signupPassword">Password</Label>
                    <div className="relative">
                      <Input
                        id="signupPassword"
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                        placeholder="Create a password"
                        required
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                    {formData.password && <PasswordStrengthIndicator />}
                  </div>
                  <div>
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        value={formData.confirmPassword}
                        onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                        placeholder="Confirm your password"
                        required
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                    {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                      <p className="text-xs text-red-600 mt-1">Passwords do not match</p>
                    )}
                  </div>
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={loading || !isPasswordStrong() || formData.password !== formData.confirmPassword}
                  >
                    {loading
                      ? 'Creating Account...'
                      : inviteContext.isInvite
                        ? 'Create Account & Join Team'
                        : 'Create Account'
                    }
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};






// import React, { useState, useEffect } from 'react';
// import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
// import { Button } from '@/components/ui/button';
// import { Input } from '@/components/ui/input';
// import { Label } from '@/components/ui/label';
// import { Alert, AlertDescription } from '@/components/ui/alert';
// import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
// import { useToast } from '@/hooks/use-toast';
// import { supabase } from '@/integrations/supabase/client';
// import { useAuth } from './AuthProvider';
// import { OrganizationSignup } from './OrganizationSignup';
// import { TokenService } from '@/services/tokenService';
// import { User, Building, AlertCircle, Eye, EyeOff, CheckCircle2, XCircle, Mail } from 'lucide-react';


// export const EnhancedAuthForm = () => {
//   const { user } = useAuth();
//   const { toast } = useToast();
//   const [mode, setMode] = useState<'auth' | 'org-signup'>('auth');
//   const [isLogin, setIsLogin] = useState(true);
//   const [isForgotPassword, setIsForgotPassword] = useState(false);
//   const [loading, setLoading] = useState(false);
//   const [showPassword, setShowPassword] = useState(false);
//   const [showConfirmPassword, setShowConfirmPassword] = useState(false);

//   // ─── INVITE CONTEXT ──────────────────────────────────────────────────────────
//   const [inviteContext, setInviteContext] = useState<{
//     isInvite: boolean;
//     orgId: string | null;
//     invitedEmail: string | null;
//   }>({ isInvite: false, orgId: null, invitedEmail: null });

//   const [passwordStrength, setPasswordStrength] = useState({
//     hasMinLength: false,
//     hasUpperCase: false,
//     hasLowerCase: false,
//     hasNumber: false,
//     hasSpecialChar: false
//   });
//   const [formData, setFormData] = useState({
//     email: '',
//     password: '',
//     fullName: '',
//     confirmPassword: ''
//   });

//   // ─── READ INVITE PARAMS FROM URL ON MOUNT ────────────────────────────────────
//   useEffect(() => {
//     const params = new URLSearchParams(window.location.search);
//     const isInvite = params.get('invite') === 'true';
//     const invitedEmail = params.get('email');
//     const orgId = params.get('org');

//     if (isInvite && invitedEmail) {
//       setInviteContext({ isInvite: true, orgId, invitedEmail });
//       setFormData(prev => ({ ...prev, email: invitedEmail }));
//       setIsLogin(false); // switch to signup tab
//     }
//   }, []);

//   useEffect(() => {
//     if (user) {
//       window.location.href = '/';
//     }
//   }, [user]);

//   useEffect(() => {
//     if (formData.password) {
//       setPasswordStrength({
//         hasMinLength: formData.password.length >= 8,
//         hasUpperCase: /[A-Z]/.test(formData.password),
//         hasLowerCase: /[a-z]/.test(formData.password),
//         hasNumber: /[0-9]/.test(formData.password),
//         hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(formData.password)
//       });
//     }
//   }, [formData.password]);

//   const isPasswordStrong = () => {
//     return Object.values(passwordStrength).every(v => v === true);
//   };

//   // ─── SEND EMAIL HELPER ───────────────────────────────────────────────────────
//   const sendEmail = async (to: string, subject: string, html: string) => {
//     const { data, error } = await supabase.functions.invoke("send-email", {
//       body: { to, subject, html },
//     });
//     if (error) throw new Error(`Failed to send email: ${error.message}`);
//     if (data && !data.success) throw new Error(data.error || "Failed to send email");
//     return data;
//   };

//   // ─── SIGN IN ─────────────────────────────────────────────────────────────────
//   const handleLogin = async (e: React.FormEvent) => {
//     e.preventDefault();

//     if (!formData.email || !formData.password) {
//       toast({
//         title: "Missing Information",
//         description: "Please fill in all required fields",
//         variant: "destructive",
//       });
//       return;
//     }

//     setLoading(true);
//     try {
//       const { error } = await supabase.auth.signInWithPassword({
//         email: formData.email,
//         password: formData.password,
//       });

//       if (error) {
//         if (error.message.includes('Invalid login credentials')) {
//           throw new Error('Invalid email or password. Please check your credentials and try again.');
//         }
//         if (error.message.includes('Email not confirmed')) {
//           throw new Error('Please verify your email address before signing in. Check your inbox for a verification link.');
//         }
//         throw error;
//       }

//       toast({
//         title: "Welcome back!",
//         description: "You have been successfully logged in.",
//       });
//     } catch (error) {
//       toast({
//         title: "Login Failed",
//         description: error instanceof Error ? error.message : "An unexpected error occurred",
//         variant: "destructive",
//       });
//     } finally {
//       setLoading(false);
//     }
//   };

//   // ─── SIGN UP ─────────────────────────────────────────────────────────────────
//   const handleSignup = async (e: React.FormEvent) => {
//     e.preventDefault();

//     if (!formData.email || !formData.password || !formData.fullName) {
//       toast({
//         title: "Missing Information",
//         description: "Please fill in all required fields",
//         variant: "destructive",
//       });
//       return;
//     }

//     if (formData.password !== formData.confirmPassword) {
//       toast({
//         title: "Password Mismatch",
//         description: "Passwords do not match",
//         variant: "destructive",
//       });
//       return;
//     }

//     if (!isPasswordStrong()) {
//       toast({
//         title: "Weak Password",
//         description: "Please meet all password requirements",
//         variant: "destructive",
//       });
//       return;
//     }

//     setLoading(true);
//     try {
//       // Step 1: Create account via custom-signup edge function
//       const { data: signUpData, error: signUpError } = await supabase.functions.invoke("custom-signup", {
//         body: {
//           email: formData.email,
//           password: formData.password,
//           fullName: formData.fullName
//         },
//       });

//       if (signUpError) {
//         let errorMessage = "An unexpected error occurred";
//         try {
//           const errorBody = await signUpError.context.json();
//           errorMessage = errorBody.error || signUpError.message;
//         } catch {
//           errorMessage = signUpError.message;
//         }
//         throw new Error(errorMessage);
//       }

//       if (signUpData?.error) {
//         throw new Error(signUpData.error);
//       }

//       // Step 2: Generate email verification token
//       const { success, token, error: tokenError } = await TokenService.createEmailVerificationToken(formData.email);

//       if (!success || !token) {
//         console.error("Token creation failed:", tokenError);
//         toast({
//           title: "Account Created!",
//           description: "Please check your email to verify your account.",
//         });
//         setFormData({ email: '', password: '', fullName: '', confirmPassword: '' });
//         return;
//       }

//       // Step 3: Build verification link
//       // KEY CHANGE: pass &org= if coming from an invite so VerifyEmailPage
//       // can activate the team_members row after verification
//       const verificationLink = inviteContext.isInvite && inviteContext.orgId
//         ? `${window.location.origin}/verify-email?token=${token}&org=${inviteContext.orgId}`
//         : `${window.location.origin}/verify-email?token=${token}`;

//       // Step 4: Send branded verification email
//       await sendEmail(
//         formData.email,
//         "Verify your GreenCard account",
//         `
//           <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
//             <div style="text-align: center; margin-bottom: 30px;">
//               <h1 style="color: #16a34a; margin: 0;">GreenCard Sustainability Manager</h1>
//               <p style="color: #666; margin: 4px 0 0;">Sustainable Business Intelligence Platform</p>
//             </div>

//             <h2 style="color: #1a1a1a;">Welcome, ${formData.fullName}! 👋</h2>

//             ${inviteContext.isInvite
//               ? `<p style="color: #444; line-height: 1.6;">
//                   You've been invited to join a GreenCard organization.
//                   Verify your email to activate your account and join the team.
//                 </p>`
//               : `<p style="color: #444; line-height: 1.6;">
//                   Thanks for signing up. Please verify your email address to activate your account.
//                 </p>`
//             }

//             <div style="text-align: center; margin: 30px 0;">
//               <a href="${verificationLink}"
//                 style="display: inline-block; padding: 14px 32px; background-color: #16a34a; color: white;
//                        text-decoration: none; border-radius: 8px; font-size: 16px; font-weight: bold;">
//                 Verify Email Address
//               </a>
//             </div>

//             <p style="color: #666; font-size: 14px; line-height: 1.6;">
//               This link will expire in <strong>24 hours</strong>.
//               If you didn't create an account, you can safely ignore this email.
//             </p>

//             <hr style="border: none; border-top: 1px solid #eee; margin: 30px 0;" />
//             <p style="color: #999; font-size: 12px; text-align: center;">
//               GreenCard Sustainability Platform · If the button doesn't work, copy this link:<br/>
//               <a href="${verificationLink}" style="color: #16a34a;">${verificationLink}</a>
//             </p>
//           </div>
//         `
//       );

//       toast({
//         title: "Account Created!",
//         description: "We've sent a verification link to your email. Please check your inbox.",
//       });

//       setFormData({ email: '', password: '', fullName: '', confirmPassword: '' });
//       setIsLogin(true);

//     } catch (error) {
//       console.error('Signup error:', error);
//       toast({
//         title: "Signup Failed",
//         description: error instanceof Error ? error.message : "An unexpected error occurred",
//         variant: "destructive",
//       });
//     } finally {
//       setLoading(false);
//     }
//   };

//   // ─── FORGOT PASSWORD ─────────────────────────────────────────────────────────
//   const handleForgotPassword = async (e: React.FormEvent) => {
//     e.preventDefault();

//     if (!formData.email) {
//       toast({
//         title: "Email Required",
//         description: "Please enter your email address to reset your password",
//         variant: "destructive",
//       });
//       return;
//     }

//     const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
//     if (!emailRegex.test(formData.email)) {
//       toast({
//         title: "Invalid Email",
//         description: "Please enter a valid email address",
//         variant: "destructive",
//       });
//       return;
//     }

//     setLoading(true);
//     try {
//       const { success, token, error } = await TokenService.createPasswordResetToken(formData.email);

//       if (!success) {
//         toast({
//           title: "Check Your Email",
//           description: "If an account exists with this email, you will receive a password reset link shortly.",
//         });
//         setIsForgotPassword(false);
//         setFormData({ email: '', password: '', fullName: '', confirmPassword: '' });
//         return;
//       }

//       if (!token) throw new Error('Failed to generate reset token');

//       const resetLink = `${window.location.origin}/reset-password?token=${token}`;

//       await sendEmail(
//         formData.email,
//         "Password Reset Request - GreenCard",
//         `
//           <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
//             <div style="text-align: center; margin-bottom: 30px;">
//               <h1 style="color: #16a34a; margin: 0;">GreenCard</h1>
//               <p style="color: #666; margin: 4px 0 0;">Sustainable Business Intelligence Platform</p>
//             </div>
//             <h2 style="color: #1a1a1a;">Reset your password</h2>
//             <p style="color: #444; line-height: 1.6;">
//               We received a request to reset the password for your GreenCard account.
//               Click the button below to choose a new password.
//             </p>
//             <div style="text-align: center; margin: 30px 0;">
//               <a href="${resetLink}"
//                 style="display: inline-block; padding: 14px 32px; background-color: #16a34a; color: white;
//                        text-decoration: none; border-radius: 8px; font-size: 16px; font-weight: bold;">
//                 Reset Password
//               </a>
//             </div>
//             <p style="color: #666; font-size: 14px; line-height: 1.6;">
//               This link will expire in <strong>1 hour</strong>. If you didn't request a password reset,
//               you can safely ignore this email.
//             </p>
//             <hr style="border: none; border-top: 1px solid #eee; margin: 30px 0;" />
//             <p style="color: #999; font-size: 12px; text-align: center;">
//               GreenCard Sustainability Platform · If the button doesn't work, copy this link:<br/>
//               <a href="${resetLink}" style="color: #16a34a;">${resetLink}</a>
//             </p>
//           </div>
//         `
//       );

//       toast({
//         title: "Reset Link Sent!",
//         description: "Check your email for the password reset link. The link will expire in 1 hour.",
//       });

//       setIsForgotPassword(false);
//       setFormData({ email: '', password: '', fullName: '', confirmPassword: '' });

//     } catch (error) {
//       console.error('Error in forgot password flow:', error);
//       toast({
//         title: "Error Sending Email",
//         description: error instanceof Error ? error.message : "Failed to send reset email. Please try again later.",
//         variant: "destructive",
//       });
//     } finally {
//       setLoading(false);
//     }
//   };

//   // ─── PASSWORD STRENGTH INDICATOR ─────────────────────────────────────────────
//   const PasswordStrengthIndicator = () => (
//     <div className="space-y-2 mt-2 text-xs">
//       <p className="font-semibold text-gray-700">Password requirements:</p>
//       <div className="space-y-1">
//         {[
//           { key: 'hasMinLength', label: 'At least 8 characters' },
//           { key: 'hasUpperCase', label: 'One uppercase letter' },
//           { key: 'hasLowerCase', label: 'One lowercase letter' },
//           { key: 'hasNumber', label: 'One number' },
//           { key: 'hasSpecialChar', label: 'One special character (!@#$%^&*...)' },
//         ].map(({ key, label }) => (
//           <div key={key} className="flex items-center gap-2">
//             {passwordStrength[key as keyof typeof passwordStrength] ? (
//               <CheckCircle2 className="h-3 w-3 text-green-600" />
//             ) : (
//               <XCircle className="h-3 w-3 text-gray-400" />
//             )}
//             <span className={passwordStrength[key as keyof typeof passwordStrength] ? "text-green-600" : "text-gray-600"}>
//               {label}
//             </span>
//           </div>
//         ))}
//       </div>
//     </div>
//   );

//   // ─── ORGANIZATION SIGNUP MODE ─────────────────────────────────────────────────
//   if (mode === 'org-signup') {
//     return (
//       <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
//         <OrganizationSignup
//           onBack={() => setMode('auth')}
//           onSuccess={() => setMode('auth')}
//         />
//       </div>
//     );
//   }

//   // ─── MAIN AUTH FORM ───────────────────────────────────────────────────────────
//   return (
//     <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
//       <div className="w-full max-w-md space-y-6">

//         {/* Logo and Header */}
//         <div className="text-center">
//           <div className="flex items-center justify-center mb-4">
//             <div className="bg-green-600 p-3 rounded-full">
//               <img src="/greencard-uploads/logo.png" alt="GreenCard Logo" className="h-8 w-8 rounded-full bg-white object-contain" />
//             </div>
//           </div>
//           <h1 className="text-3xl font-bold text-gray-900">GreenCard Sustainability Manager</h1>
//           <p className="text-gray-600 mt-2">Sustainable Business Intelligence Platform</p>
//         </div>

//         {/* Invite Banner - only shown when coming from team invite */}
//         {inviteContext.isInvite && (
//           <Alert className="border-green-200 bg-green-50">
//             <Mail className="h-4 w-4 text-green-600" />
//             <AlertDescription className="text-green-800">
//               You've been invited to join a GreenCard organization. Complete your signup below to accept the invitation.
//             </AlertDescription>
//           </Alert>
//         )}

//         {/* Account Type Selection - hidden when coming from invite */}
//         {!inviteContext.isInvite && (
//           <div className="grid grid-cols-2 gap-4">
//             <Card
//               className="cursor-pointer hover:shadow-md transition-shadow border-2 border-transparent hover:border-green-200"
//               onClick={() => setMode('auth')}
//             >
//               <CardContent className="p-4 text-center">
//                 <User className="h-8 w-8 mx-auto mb-2 text-green-600" />
//                 <h3 className="font-semibold">Individual</h3>
//                 <p className="text-sm text-gray-600">Personal sustainability tracking</p>
//               </CardContent>
//             </Card>

//             <Card
//               className="cursor-pointer hover:shadow-md transition-shadow border-2 border-transparent hover:border-blue-200"
//               onClick={() => setMode('org-signup')}
//             >
//               <CardContent className="p-4 text-center">
//                 <Building className="h-8 w-8 mx-auto mb-2 text-blue-600" />
//                 <h3 className="font-semibold">Organization</h3>
//                 <p className="text-sm text-gray-600">Team collaboration & management</p>
//               </CardContent>
//             </Card>
//           </div>
//         )}

//         {/* Auth Form */}
//         <Card>
//           <CardHeader>
//             <CardTitle className="text-center">
//               {inviteContext.isInvite ? 'Accept Invitation' : 'Individual Account'}
//             </CardTitle>
//           </CardHeader>
//           <CardContent>
//             <Tabs
//               value={isLogin ? "signin" : "signup"}
//               onValueChange={(value) => {
//                 setIsLogin(value === "signin");
//                 setIsForgotPassword(false);
//               }}
//             >
//               <TabsList className="grid w-full grid-cols-2">
//                 {/* Disable Sign In tab when coming from invite - they must sign up first */}
//                 <TabsTrigger value="signin" disabled={inviteContext.isInvite}>
//                   Sign In
//                 </TabsTrigger>
//                 <TabsTrigger value="signup">Sign Up</TabsTrigger>
//               </TabsList>

//               {/* ── SIGN IN TAB ── */}
//               <TabsContent value="signin" className="space-y-4 mt-6">
//                 {isForgotPassword ? (
//                   <form onSubmit={handleForgotPassword} className="space-y-4">
//                     <Alert>
//                       <AlertCircle className="h-4 w-4" />
//                       <AlertDescription>
//                         Enter your email address and we'll send you a link to reset your password.
//                       </AlertDescription>
//                     </Alert>
//                     <div>
//                       <Label htmlFor="resetEmail">Email</Label>
//                       <Input
//                         id="resetEmail"
//                         type="email"
//                         value={formData.email}
//                         onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
//                         placeholder="Enter your email"
//                         required
//                       />
//                     </div>
//                     <Button type="submit" className="w-full" disabled={loading}>
//                       {loading ? 'Sending Reset Link...' : 'Send Reset Link'}
//                     </Button>
//                     <Button
//                       type="button"
//                       variant="link"
//                       className="w-full"
//                       onClick={() => {
//                         setIsForgotPassword(false);
//                         setFormData({ email: '', password: '', fullName: '', confirmPassword: '' });
//                       }}
//                     >
//                       Back to Sign In
//                     </Button>
//                   </form>
//                 ) : (
//                   <form onSubmit={handleLogin} className="space-y-4">
//                     <div>
//                       <Label htmlFor="email">Email</Label>
//                       <Input
//                         id="email"
//                         type="email"
//                         value={formData.email}
//                         onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
//                         placeholder="Enter your email"
//                         required
//                       />
//                     </div>
//                     <div>
//                       <div className="flex items-center justify-between">
//                         <Label htmlFor="password">Password</Label>
//                         <Button
//                           type="button"
//                           variant="link"
//                           className="px-0 h-auto text-xs"
//                           onClick={() => setIsForgotPassword(true)}
//                         >
//                           Forgot password?
//                         </Button>
//                       </div>
//                       <div className="relative">
//                         <Input
//                           id="password"
//                           type={showPassword ? "text" : "password"}
//                           value={formData.password}
//                           onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
//                           placeholder="Enter your password"
//                           required
//                         />
//                         <Button
//                           type="button"
//                           variant="ghost"
//                           size="sm"
//                           className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
//                           onClick={() => setShowPassword(!showPassword)}
//                         >
//                           {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
//                         </Button>
//                       </div>
//                     </div>
//                     <Button type="submit" className="w-full" disabled={loading}>
//                       {loading ? 'Signing In...' : 'Sign In'}
//                     </Button>
//                   </form>
//                 )}
//               </TabsContent>

//               {/* ── SIGN UP TAB ── */}
//               <TabsContent value="signup" className="space-y-4 mt-6">
//                 <form onSubmit={handleSignup} className="space-y-4">
//                   <div>
//                     <Label htmlFor="fullName">Full Name</Label>
//                     <Input
//                       id="fullName"
//                       value={formData.fullName}
//                       onChange={(e) => setFormData(prev => ({ ...prev, fullName: e.target.value }))}
//                       placeholder="Enter your full name"
//                       required
//                     />
//                   </div>
//                   <div>
//                     <Label htmlFor="signupEmail">Email</Label>
//                     <Input
//                       id="signupEmail"
//                       type="email"
//                       value={formData.email}
//                       onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
//                       placeholder="Enter your email"
//                       readOnly={inviteContext.isInvite}
//                       className={inviteContext.isInvite ? "bg-gray-50 cursor-not-allowed" : ""}
//                       required
//                     />
//                     {inviteContext.isInvite && (
//                       <p className="text-xs text-gray-500 mt-1">
//                         Email is pre-filled from your invitation and cannot be changed.
//                       </p>
//                     )}
//                   </div>
//                   <div>
//                     <Label htmlFor="signupPassword">Password</Label>
//                     <div className="relative">
//                       <Input
//                         id="signupPassword"
//                         type={showPassword ? "text" : "password"}
//                         value={formData.password}
//                         onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
//                         placeholder="Create a password"
//                         required
//                       />
//                       <Button
//                         type="button"
//                         variant="ghost"
//                         size="sm"
//                         className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
//                         onClick={() => setShowPassword(!showPassword)}
//                       >
//                         {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
//                       </Button>
//                     </div>
//                     {formData.password && <PasswordStrengthIndicator />}
//                   </div>
//                   <div>
//                     <Label htmlFor="confirmPassword">Confirm Password</Label>
//                     <div className="relative">
//                       <Input
//                         id="confirmPassword"
//                         type={showConfirmPassword ? "text" : "password"}
//                         value={formData.confirmPassword}
//                         onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
//                         placeholder="Confirm your password"
//                         required
//                       />
//                       <Button
//                         type="button"
//                         variant="ghost"
//                         size="sm"
//                         className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
//                         onClick={() => setShowConfirmPassword(!showConfirmPassword)}
//                       >
//                         {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
//                       </Button>
//                     </div>
//                     {formData.confirmPassword && formData.password !== formData.confirmPassword && (
//                       <p className="text-xs text-red-600 mt-1">Passwords do not match</p>
//                     )}
//                   </div>
//                   <Button
//                     type="submit"
//                     className="w-full"
//                     disabled={loading || !isPasswordStrong() || formData.password !== formData.confirmPassword}
//                   >
//                     {loading
//                       ? 'Creating Account...'
//                       : inviteContext.isInvite
//                         ? 'Create Account & Join Team'
//                         : 'Create Account'
//                     }
//                   </Button>
//                 </form>
//               </TabsContent>
//             </Tabs>
//           </CardContent>
//         </Card>
//       </div>
//     </div>
//   );
// };






// import React, { useState, useEffect } from 'react';
// import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
// import { Button } from '@/components/ui/button';
// import { Input } from '@/components/ui/input';
// import { Label } from '@/components/ui/label';
// import { Alert, AlertDescription } from '@/components/ui/alert';
// import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
// import { useToast } from '@/hooks/use-toast';
// import { supabase } from '@/integrations/supabase/client';
// import { useAuth } from './AuthProvider';
// import { OrganizationSignup } from './OrganizationSignup';
// import { TokenService } from '@/services/tokenService';
// import { Leaf, User, Building, AlertCircle, Eye, EyeOff, CheckCircle2, XCircle } from 'lucide-react';


// export const EnhancedAuthForm = () => {
//   const { user } = useAuth();
//   const { toast } = useToast();
//   const [mode, setMode] = useState<'auth' | 'org-signup'>('auth');
//   const [isLogin, setIsLogin] = useState(true);
//   const [isForgotPassword, setIsForgotPassword] = useState(false);
//   const [loading, setLoading] = useState(false);
//   const [showPassword, setShowPassword] = useState(false);
//   const [showConfirmPassword, setShowConfirmPassword] = useState(false);
//   const [passwordStrength, setPasswordStrength] = useState({
//     hasMinLength: false,
//     hasUpperCase: false,
//     hasLowerCase: false,
//     hasNumber: false,
//     hasSpecialChar: false
//   });
//   const [formData, setFormData] = useState({
//     email: '',
//     password: '',
//     fullName: '',
//     confirmPassword: ''
//   });

//   useEffect(() => {
//     // Only redirect to home if user is logged in
//     if (user) {
//       window.location.href = '/';
//     }
//   }, [user]);

//   // Password strength validation
//   useEffect(() => {
//     if (formData.password) {
//       setPasswordStrength({
//         hasMinLength: formData.password.length >= 8,
//         hasUpperCase: /[A-Z]/.test(formData.password),
//         hasLowerCase: /[a-z]/.test(formData.password),
//         hasNumber: /[0-9]/.test(formData.password),
//         hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(formData.password)
//       });
//     }
//   }, [formData.password]);

//   const isPasswordStrong = () => {
//     return Object.values(passwordStrength).every(v => v === true);
//   };

//   const handleSubmit = async (e: React.FormEvent) => {
//     e.preventDefault();

//     if (!formData.email || !formData.password) {
//       toast({
//         title: "Missing Information",
//         description: "Please fill in all required fields",
//         variant: "destructive",
//       });
//       return;
//     }

//     if (!isLogin) {
//       if (formData.password !== formData.confirmPassword) {
//         toast({
//           title: "Password Mismatch",
//           description: "Passwords do not match",
//           variant: "destructive",
//         });
//         return;
//       }

//       if (!isPasswordStrong()) {
//         toast({
//           title: "Weak Password",
//           description: "Please meet all password requirements",
//           variant: "destructive",
//         });
//         return;
//       }
//     }

//     setLoading(true);

//     try {
//       if (isLogin) {
//         const { error } = await supabase.auth.signInWithPassword({
//           email: formData.email,
//           password: formData.password,
//         });

//         if (error) {
//           if (error.message.includes('Invalid login credentials')) {
//             throw new Error('Invalid email or password. Please check your credentials and try again.');
//           }
//           throw error;
//         }

//         toast({
//           title: "Welcome back!",
//           description: "You have been successfully logged in.",
//         });
//       } else {
//         const { error } = await supabase.auth.signUp({
//           email: formData.email,
//           password: formData.password,
//           options: {
//             emailRedirectTo: `${window.location.origin}/auth`,
//             data: {
//               full_name: formData.fullName
//             }
//           }
//         });

//         if (error) {
//           if (error.message.includes('User already registered')) {
//             throw new Error('An account with this email already exists. Please sign in instead.');
//           }
//           throw error;
//         }

//         toast({
//           title: "Account Created!",
//           description: "Please check your email to verify your account.",
//         });
//       }
//     } catch (error) {
//       toast({
//         title: isLogin ? "Login Failed" : "Signup Failed",
//         description: error instanceof Error ? error.message : "An unexpected error occurred",
//         variant: "destructive",
//       });
//     } finally {
//       setLoading(false);
//     }
//   };


//   const handleForgotPassword = async (e: React.FormEvent) => {
//     e.preventDefault();
//     if (!formData.email) {
//       toast({
//         title: "Email Required",
//         description: "Please enter your email address to reset your password",
//         variant: "destructive",
//       });
//       return;
//     }

//     // Basic email validation
//     const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
//     if (!emailRegex.test(formData.email)) {
//       toast({
//         title: "Invalid Email",
//         description: "Please enter a valid email address",
//         variant: "destructive",
//       });
//       return;
//     }

//     setLoading(true);
//     try {
//       // Create password reset token using the TokenService
//       const { success, token, error } = await TokenService.createPasswordResetToken(formData.email);
      
//       if (!success) {
//         console.error("Token creation failed:", error);
//         // For security, don't reveal if user exists or not
//         toast({
//           title: "Check Your Email",
//           description: "If an account exists with this email, you will receive a password reset link shortly.",
//         });
//         setIsForgotPassword(false);
//         setFormData({ email: '', password: '', fullName: '', confirmPassword: '' });
//         return;
//       }

//       if (!token) {
//         throw new Error('Failed to generate reset token');
//       }

//       // Generate reset link for the separate reset password page
//       const resetLink = `${window.location.origin}/reset-password?token=${token}`;

//       // Call the edge function to send email
//       const { data, error: passwordResetError } = await supabase.functions.invoke("send-email", {
//         body: {
//           to: formData.email,
//           subject: "Password Reset Request - GreenCard",
//           html: `
//             <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
//               <h1 style="color: #16a34a;">Password Reset Request</h1>
//               <p>You requested to reset your password for your GreenCard Sustainability Manager account.</p>
//               <p>Click the button below to reset your password:</p>
//               <a href="${resetLink}" 
//                 style="display: inline-block; padding: 12px 24px; background-color: #16a34a; color: white; text-decoration: none; border-radius: 6px; margin: 20px 0;">
//                 Reset Password
//               </a>
//               <p style="color: #666; font-size: 14px;">This link will expire in 1 hour.</p>
//               <p style="color: #666; font-size: 14px;">If you didn't request this, you can safely ignore this email.</p>
//             </div>
//           `,
//         },
//       });


//       if (passwordResetError) {
//         console.error("Email function error:", passwordResetError);
//         throw new Error(`Email sending failed: ${passwordResetError.message || JSON.stringify(passwordResetError)}`);
//       }

//       // Check if the response indicates failure
//       if (data && !data.success) {
//         console.error("Email sending reported failure:", data);
//         throw new Error(data.error || "Email sending failed");
//       }
      
//       toast({
//         title: "Reset Link Sent!",
//         description: 'Check your email for the password reset link. The link will expire in 1 hour.',
//       });
      
//       setIsForgotPassword(false);

//       setFormData({ email: '', password: '', fullName: '', confirmPassword: '' });
      
//     } catch (error) {
//       console.error('Error in forgot password flow:', error);
//       toast({
//         title: "Error Sending Email",
//         description: error instanceof Error ? error.message : "Failed to send reset email. Please try again later.",
//         variant: "destructive",
//       });
//     } finally {
//       setLoading(false);
//     }
//   };
  
  
//   const PasswordStrengthIndicator = () => (
//     <div className="space-y-2 mt-2 text-xs">
//       <p className="font-semibold text-gray-700">Password requirements:</p>
//       <div className="space-y-1">
//         <div className="flex items-center gap-2">
//           {passwordStrength.hasMinLength ? (
//             <CheckCircle2 className="h-3 w-3 text-green-600" />
//           ) : (
//             <XCircle className="h-3 w-3 text-gray-400" />
//           )}
//           <span className={passwordStrength.hasMinLength ? "text-green-600" : "text-gray-600"}>
//             At least 8 characters
//           </span>
//         </div>
//         <div className="flex items-center gap-2">
//           {passwordStrength.hasUpperCase ? (
//             <CheckCircle2 className="h-3 w-3 text-green-600" />
//           ) : (
//             <XCircle className="h-3 w-3 text-gray-400" />
//           )}
//           <span className={passwordStrength.hasUpperCase ? "text-green-600" : "text-gray-600"}>
//             One uppercase letter
//           </span>
//         </div>
//         <div className="flex items-center gap-2">
//           {passwordStrength.hasLowerCase ? (
//             <CheckCircle2 className="h-3 w-3 text-green-600" />
//           ) : (
//             <XCircle className="h-3 w-3 text-gray-400" />
//           )}
//           <span className={passwordStrength.hasLowerCase ? "text-green-600" : "text-gray-600"}>
//             One lowercase letter
//           </span>
//         </div>
//         <div className="flex items-center gap-2">
//           {passwordStrength.hasNumber ? (
//             <CheckCircle2 className="h-3 w-3 text-green-600" />
//           ) : (
//             <XCircle className="h-3 w-3 text-gray-400" />
//           )}
//           <span className={passwordStrength.hasNumber ? "text-green-600" : "text-gray-600"}>
//             One number
//           </span>
//         </div>
//         <div className="flex items-center gap-2">
//           {passwordStrength.hasSpecialChar ? (
//             <CheckCircle2 className="h-3 w-3 text-green-600" />
//           ) : (
//             <XCircle className="h-3 w-3 text-gray-400" />
//           )}
//           <span className={passwordStrength.hasSpecialChar ? "text-green-600" : "text-gray-600"}>
//             One special character (!@#$%^&*...)
//           </span>
//         </div>
//       </div>
//     </div>
//   );

//   // Organization signup mode
//   if (mode === 'org-signup') {
//     return (
//       <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
//         <OrganizationSignup
//           onBack={() => setMode('auth')}
//           onSuccess={() => setMode('auth')}
//         />
//       </div>
//     );
//   }

//   // Main auth mode
//   return (
//     <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
//       <div className="w-full max-w-md space-y-6">
//         {/* Logo and Header */}
//         <div className="text-center">
//           <div className="flex items-center justify-center mb-4">
//             <div className="bg-green-600 p-3 rounded-full">
//               <img src="/greencard-uploads/logo.png" alt="GreenCard Logo" className="h-8 w-8 rounded-full bg-white object-contain" />
//             </div>
//           </div>
//           <h1 className="text-3xl font-bold text-gray-900">GreenCard</h1>
//           <p className="text-gray-600 mt-2">Sustainable Business Intelligence Platform</p>
//         </div>

//         {/* Signup Type Selection */}
//         <div className="grid grid-cols-2 gap-4">
//           <Card
//             className="cursor-pointer hover:shadow-md transition-shadow border-2 border-transparent hover:border-green-200"
//             onClick={() => setMode('auth')}
//           >
//             <CardContent className="p-4 text-center">
//               <User className="h-8 w-8 mx-auto mb-2 text-green-600" />
//               <h3 className="font-semibold">Individual</h3>
//               <p className="text-sm text-gray-600">Personal sustainability tracking</p>
//             </CardContent>
//           </Card>

//           <Card
//             className="cursor-pointer hover:shadow-md transition-shadow border-2 border-transparent hover:border-blue-200"
//             onClick={() => setMode('org-signup')}
//           >
//             <CardContent className="p-4 text-center">
//               <Building className="h-8 w-8 mx-auto mb-2 text-blue-600" />
//               <h3 className="font-semibold">Organization</h3>
//               <p className="text-sm text-gray-600">Team collaboration & management</p>
//             </CardContent>
//           </Card>
//         </div>

//         {/* Auth Form */}
//         <Card>
//           <CardHeader>
//             <CardTitle className="text-center">
//               Individual Account
//             </CardTitle>
//           </CardHeader>
//           <CardContent>
//             <Tabs value={isLogin ? "signin" : "signup"} onValueChange={(value) => setIsLogin(value === "signin")}>
//               <TabsList className="grid w-full grid-cols-2">
//                 <TabsTrigger value="signin" onClick={() => setIsForgotPassword(false)}>Sign In</TabsTrigger>
//                 <TabsTrigger value="signup" onClick={() => setIsForgotPassword(false)}>Sign Up</TabsTrigger>
//               </TabsList>

//               <TabsContent value="signin" className="space-y-4 mt-6">
//                 {isForgotPassword ? (
//                   <form onSubmit={handleForgotPassword} className="space-y-4">
//                     <Alert>
//                       <AlertCircle className="h-4 w-4" />
//                       <AlertDescription>
//                         Enter your email address and we'll send you a link to reset your password.
//                       </AlertDescription>
//                     </Alert>

//                     <div>
//                       <Label htmlFor="resetEmail">Email</Label>
//                       <Input
//                         id="resetEmail"
//                         type="email"
//                         value={formData.email}
//                         onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
//                         placeholder="Enter your email"
//                         required
//                       />
//                     </div>
//                     <Button type="submit" className="w-full" disabled={loading}>
//                       {loading ? 'Sending Reset Link...' : 'Send Reset Link'}
//                     </Button>
//                     <Button
//                       type="button"
//                       variant="link"
//                       className="w-full"
//                       onClick={() => {
//                         setIsForgotPassword(false);
//                         setFormData({ email: '', password: '', fullName: '', confirmPassword: '' });
//                       }}
//                     >
//                       Back to Sign In
//                     </Button>
//                   </form>
//                 ) : (
//                   <form onSubmit={handleSubmit} className="space-y-4">
//                     <div>
//                       <Label htmlFor="email">Email</Label>
//                       <Input
//                         id="email"
//                         type="email"
//                         value={formData.email}
//                         onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
//                         placeholder="Enter your email"
//                         required
//                       />
//                     </div>

//                     <div>
//                       <div className="flex items-center justify-between">
//                         <Label htmlFor="password">Password</Label>
//                         <Button
//                           type="button"
//                           variant="link"
//                           className="px-0 h-auto text-xs"
//                           onClick={() => setIsForgotPassword(true)}
//                         >
//                           Forgot password?
//                         </Button>
//                       </div>
//                       <div className="relative">
//                         <Input
//                           id="password"
//                           type={showPassword ? "text" : "password"}
//                           value={formData.password}
//                           onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
//                           placeholder="Enter your password"
//                           required
//                         />
//                         <Button
//                           type="button"
//                           variant="ghost"
//                           size="sm"
//                           className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
//                           onClick={() => setShowPassword(!showPassword)}
//                         >
//                           {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
//                         </Button>
//                       </div>
//                     </div>

//                     <Button type="submit" className="w-full" disabled={loading}>
//                       {loading ? 'Signing In...' : 'Sign In'}
//                     </Button>
//                   </form>
//                 )}
//               </TabsContent>

//               <TabsContent value="signup" className="space-y-4 mt-6">
//                 <form onSubmit={handleSubmit} className="space-y-4">
//                   <div>
//                     <Label htmlFor="fullName">Full Name</Label>
//                     <Input
//                       id="fullName"
//                       value={formData.fullName}
//                       onChange={(e) => setFormData(prev => ({ ...prev, fullName: e.target.value }))}
//                       placeholder="Enter your full name"
//                       required
//                     />
//                   </div>

//                   <div>
//                     <Label htmlFor="signupEmail">Email</Label>
//                     <Input
//                       id="signupEmail"
//                       type="email"
//                       value={formData.email}
//                       onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
//                       placeholder="Enter your email"
//                       required
//                     />
//                   </div>

//                   <div>
//                     <Label htmlFor="signupPassword">Password</Label>
//                     <div className="relative">
//                       <Input
//                         id="signupPassword"
//                         type={showPassword ? "text" : "password"}
//                         value={formData.password}
//                         onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
//                         placeholder="Create a password"
//                         required
//                       />
//                       <Button
//                         type="button"
//                         variant="ghost"
//                         size="sm"
//                         className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
//                         onClick={() => setShowPassword(!showPassword)}
//                       >
//                         {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
//                       </Button>
//                     </div>
//                     {formData.password && <PasswordStrengthIndicator />}
//                   </div>

//                   <div>
//                     <Label htmlFor="confirmPassword">Confirm Password</Label>
//                     <div className="relative">
//                       <Input
//                         id="confirmPassword"
//                         type={showConfirmPassword ? "text" : "password"}
//                         value={formData.confirmPassword}
//                         onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
//                         placeholder="Confirm your password"
//                         required
//                       />
//                       <Button
//                         type="button"
//                         variant="ghost"
//                         size="sm"
//                         className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
//                         onClick={() => setShowConfirmPassword(!showConfirmPassword)}
//                       >
//                         {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
//                       </Button>
//                     </div>
//                     {formData.confirmPassword && formData.password !== formData.confirmPassword && (
//                       <p className="text-xs text-red-600 mt-1">Passwords do not match</p>
//                     )}
//                   </div>

//                   <Button
//                     type="submit"
//                     className="w-full"
//                     disabled={loading || !isPasswordStrong() || formData.password !== formData.confirmPassword}
//                   >
//                     {loading ? 'Creating Account...' : 'Create Account'}
//                   </Button>
//                 </form>
//               </TabsContent>
//             </Tabs>

//             {/* <Alert className="mt-4">
//               <AlertCircle className="h-4 w-4" />
//               <AlertDescription>
//                 After creating your account, you'll be prompted to connect your storage location for data isolation and cross-device sync.
//               </AlertDescription>
//             </Alert> */}
//           </CardContent>
//         </Card>
//       </div>
//     </div>
//   );
// };






// // import React, { useState, useEffect } from 'react';
// // import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
// // import { Button } from '@/components/ui/button';
// // import { Input } from '@/components/ui/input';
// // import { Label } from '@/components/ui/label';
// // import { Alert, AlertDescription } from '@/components/ui/alert';
// // import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
// // import { useToast } from '@/hooks/use-toast';
// // import { supabase } from '@/integrations/supabase/client';
// // import { useAuth } from './AuthProvider';
// // import { OrganizationSignup } from './OrganizationSignup';
// // import { Leaf, User, Building, AlertCircle, Eye, EyeOff } from 'lucide-react';

// // export const EnhancedAuthForm = () => {
// //   const { user } = useAuth();
// //   const { toast } = useToast();
// //   const [mode, setMode] = useState<'auth' | 'org-signup' | 'reset-password'>('auth');
// //   const [isLogin, setIsLogin] = useState(true);
// //   const [isForgotPassword, setIsForgotPassword] = useState(false);
// //   const [loading, setLoading] = useState(false);
// //   const [showPassword, setShowPassword] = useState(false);
// //   const [formData, setFormData] = useState({
// //     email: '',
// //     password: '',
// //     fullName: '',
// //     confirmPassword: ''
// //   });

// //   useEffect(() => {
// //     if (user && mode !== 'reset-password') {
// //       window.location.href = '/';
// //     }

// //     // Check for reset password mode in URL
// //     const params = new URLSearchParams(window.location.search);
// //     if (params.get('mode') === 'reset_password') {
// //       setMode('reset-password');
// //     }
// //   }, [user, mode]);

// //   const handleSubmit = async (e: React.FormEvent) => {
// //     e.preventDefault();

// //     if (!formData.email || !formData.password) {
// //       toast({
// //         title: "Missing Information",
// //         description: "Please fill in all required fields",
// //         variant: "destructive",
// //       });
// //       return;
// //     }

// //     if (!isLogin && formData.password !== formData.confirmPassword) {
// //       toast({
// //         title: "Password Mismatch",
// //         description: "Passwords do not match",
// //         variant: "destructive",
// //       });
// //       return;
// //     }

// //     setLoading(true);

// //     try {
// //       if (isLogin) {
// //         const { error } = await supabase.auth.signInWithPassword({
// //           email: formData.email,
// //           password: formData.password,
// //         });

// //         if (error) {
// //           if (error.message.includes('Invalid login credentials')) {
// //             throw new Error('Invalid email or password. Please check your credentials and try again.');
// //           }
// //           throw error;
// //         }

// //         toast({
// //           title: "Welcome back!",
// //           description: "You have been successfully logged in.",
// //         });
// //       } else {
// //         const { error } = await supabase.auth.signUp({
// //           email: formData.email,
// //           password: formData.password,
// //           options: {
// //             emailRedirectTo: `${window.location.origin}/`,
// //             data: {
// //               full_name: formData.fullName
// //             }
// //           }
// //         });

// //         if (error) {
// //           if (error.message.includes('User already registered')) {
// //             throw new Error('An account with this email already exists. Please sign in instead.');
// //           }
// //           throw error;
// //         }

// //         toast({
// //           title: "Account Created!",
// //           description: "Please check your email to verify your account.",
// //         });
// //       }
// //     } catch (error) {
// //       toast({
// //         title: isLogin ? "Login Failed" : "Signup Failed",
// //         description: error instanceof Error ? error.message : "An unexpected error occurred",
// //         variant: "destructive",
// //       });
// //     } finally {
// //       setLoading(false);
// //     }
// //   };

// //   const handleForgotPassword = async (e: React.FormEvent) => {
// //     e.preventDefault();
// //     if (!formData.email) {
// //       toast({
// //         title: "Email Required",
// //         description: "Please enter your email address to reset your password",
// //         variant: "destructive",
// //       });
// //       return;
// //     }

// //     setLoading(true);
// //     try {
// //       const { error } = await supabase.auth.resetPasswordForEmail(formData.email, {
// //         redirectTo: `${window.location.origin}/auth?mode=reset_password`,
// //       });

// //       if (error) throw error;

// //       toast({
// //         title: "Reset Link Sent",
// //         description: "Check your email for the password reset link.",
// //       });
// //       setIsForgotPassword(false);
// //     } catch (error) {
// //       toast({
// //         title: "Error",
// //         description: error instanceof Error ? error.message : "Failed to send reset email",
// //         variant: "destructive",
// //       });
// //     } finally {
// //       setLoading(false);
// //     }
// //   };

// //   const handleUpdatePassword = async (e: React.FormEvent) => {
// //     e.preventDefault();
// //     if (!formData.password || formData.password !== formData.confirmPassword) {
// //       toast({
// //         title: "Invalid Input",
// //         description: "Passwords must match and cannot be empty",
// //         variant: "destructive",
// //       });
// //       return;
// //     }

// //     setLoading(true);
// //     try {
// //       const { error } = await supabase.auth.updateUser({
// //         password: formData.password
// //       });

// //       if (error) throw error;

// //       toast({
// //         title: "Password Updated",
// //         description: "Your password has been successfully updated. You can now sign in.",
// //       });
// //       setMode('auth');
// //       setIsLogin(true);
// //     } catch (error) {
// //       toast({
// //         title: "Update Failed",
// //         description: error instanceof Error ? error.message : "Failed to update password",
// //         variant: "destructive",
// //       });
// //     } finally {
// //       setLoading(false);
// //     }
// //   };

// //   if (mode === 'org-signup') {
// //     return (
// //       <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
// //         <OrganizationSignup
// //           onBack={() => setMode('auth')}
// //           onSuccess={() => setMode('auth')}
// //         />
// //       </div>
// //     );
// //   }

// //   return (
// //     <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
// //       <div className="w-full max-w-md space-y-6">
// //         {/* Logo and Header */}
// //         <div className="text-center">
// //           <div className="flex items-center justify-center mb-4">
// //             <div className="bg-green-600 p-3 rounded-full">
// //               <img src="/greencard-uploads/logo.png" alt="GreenCard Logo" className="h-8 w-8 rounded-full bg-white object-contain" />
// //             </div>
// //           </div>
// //           <h1 className="text-3xl font-bold text-gray-900">GreenCard</h1>
// //           <p className="text-gray-600 mt-2">Sustainable Business Intelligence Platform</p>
// //         </div>

// //         {/* Signup Type Selection */}
// //         <div className="grid grid-cols-2 gap-4">
// //           <Card
// //             className="cursor-pointer hover:shadow-md transition-shadow border-2 border-transparent hover:border-green-200"
// //             onClick={() => setMode('auth')}
// //           >
// //             <CardContent className="p-4 text-center">
// //               <User className="h-8 w-8 mx-auto mb-2 text-green-600" />
// //               <h3 className="font-semibold">Individual</h3>
// //               <p className="text-sm text-gray-600">Personal sustainability tracking</p>
// //             </CardContent>
// //           </Card>

// //           <Card
// //             className="cursor-pointer hover:shadow-md transition-shadow border-2 border-transparent hover:border-blue-200"
// //             onClick={() => setMode('org-signup')}
// //           >
// //             <CardContent className="p-4 text-center">
// //               <Building className="h-8 w-8 mx-auto mb-2 text-blue-600" />
// //               <h3 className="font-semibold">Organization</h3>
// //               <p className="text-sm text-gray-600">Team collaboration & management</p>
// //             </CardContent>
// //           </Card>
// //         </div>

// //         {/* Auth Form */}
// //         <Card>
// //           <CardHeader>
// //             <CardTitle className="text-center">
// //               Individual Account
// //             </CardTitle>
// //           </CardHeader>
// //           <CardContent>
// //             <Tabs value={isLogin ? "signin" : "signup"} onValueChange={(value) => setIsLogin(value === "signin")}>
// //               <TabsList className="grid w-full grid-cols-2">
// //                 <TabsTrigger value="signin" onClick={() => setIsForgotPassword(false)}>Sign In</TabsTrigger>
// //                 <TabsTrigger value="signup" onClick={() => setIsForgotPassword(false)}>Sign Up</TabsTrigger>
// //               </TabsList>

// //               <TabsContent value="signin" className="space-y-4 mt-6">
// //                 {isForgotPassword ? (
// //                   <form onSubmit={handleForgotPassword} className="space-y-4">
// //                     <div>
// //                       <Label htmlFor="resetEmail">Email</Label>
// //                       <Input
// //                         id="resetEmail"
// //                         type="email"
// //                         value={formData.email}
// //                         onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
// //                         placeholder="Enter your email"
// //                         required
// //                       />
// //                     </div>
// //                     <Button type="submit" className="w-full" disabled={loading}>
// //                       {loading ? 'Sending Link...' : 'Send Reset Link'}
// //                     </Button>
// //                     <Button
// //                       type="button"
// //                       variant="link"
// //                       className="w-full"
// //                       onClick={() => setIsForgotPassword(false)}
// //                     >
// //                       Back to Sign In
// //                     </Button>
// //                   </form>
// //                 ) : (
// //                   <form onSubmit={handleSubmit} className="space-y-4">
// //                     <div>
// //                       <Label htmlFor="email">Email</Label>
// //                       <Input
// //                         id="email"
// //                         type="email"
// //                         value={formData.email}
// //                         onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
// //                         placeholder="Enter your email"
// //                         required
// //                       />
// //                     </div>

// //                     <div>
// //                       <div className="flex items-center justify-between">
// //                         <Label htmlFor="password">Password</Label>
// //                         <Button
// //                           type="button"
// //                           variant="link"
// //                           className="px-0 h-auto text-xs"
// //                           onClick={() => setIsForgotPassword(true)}
// //                         >
// //                           Forgot password?
// //                         </Button>
// //                       </div>
// //                       <div className="relative">
// //                         <Input
// //                           id="password"
// //                           type={showPassword ? "text" : "password"}
// //                           value={formData.password}
// //                           onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
// //                           placeholder="Enter your password"
// //                           required
// //                         />
// //                         <Button
// //                           type="button"
// //                           variant="ghost"
// //                           size="sm"
// //                           className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
// //                           onClick={() => setShowPassword(!showPassword)}
// //                         >
// //                           {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
// //                         </Button>
// //                       </div>
// //                     </div>

// //                     <Button type="submit" className="w-full" disabled={loading}>
// //                       {loading ? 'Signing In...' : 'Sign In'}
// //                     </Button>
// //                   </form>
// //                 )}
// //               </TabsContent>

// //               <TabsContent value="signup" className="space-y-4 mt-6">
// //                 <form onSubmit={handleSubmit} className="space-y-4">
// //                   <div>
// //                     <Label htmlFor="fullName">Full Name</Label>
// //                     <Input
// //                       id="fullName"
// //                       value={formData.fullName}
// //                       onChange={(e) => setFormData(prev => ({ ...prev, fullName: e.target.value }))}
// //                       placeholder="Enter your full name"
// //                       required
// //                     />
// //                   </div>

// //                   <div>
// //                     <Label htmlFor="signupEmail">Email</Label>
// //                     <Input
// //                       id="signupEmail"
// //                       type="email"
// //                       value={formData.email}
// //                       onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
// //                       placeholder="Enter your email"
// //                       required
// //                     />
// //                   </div>

// //                   <div>
// //                     <Label htmlFor="signupPassword">Password</Label>
// //                     <div className="relative">
// //                       <Input
// //                         id="signupPassword"
// //                         type={showPassword ? "text" : "password"}
// //                         value={formData.password}
// //                         onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
// //                         placeholder="Create a password"
// //                         required
// //                       />
// //                       <Button
// //                         type="button"
// //                         variant="ghost"
// //                         size="sm"
// //                         className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
// //                         onClick={() => setShowPassword(!showPassword)}
// //                       >
// //                         {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
// //                       </Button>
// //                     </div>
// //                   </div>

// //                   <div>
// //                     <Label htmlFor="confirmPassword">Confirm Password</Label>
// //                     <Input
// //                       id="confirmPassword"
// //                       type="password"
// //                       value={formData.confirmPassword}
// //                       onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
// //                       placeholder="Confirm your password"
// //                       required
// //                     />
// //                   </div>

// //                   <Button type="submit" className="w-full" disabled={loading}>
// //                     {loading ? 'Creating Account...' : 'Create Account'}
// //                   </Button>
// //                 </form>
// //               </TabsContent>
// //             </Tabs>

// //             <Alert className="mt-4">
// //               <AlertCircle className="h-4 w-4" />
// //               <AlertDescription>
// //                 After creating your account, you'll be prompted to connect your storage location for data isolation and cross-device sync.
// //               </AlertDescription>
// //             </Alert>
// //           </CardContent>
// //         </Card>
// //       </div>
// //     </div>
// //   );
// // };
